/** Portfolio Website — light pastel personal-brand theme (editorial, non-dashboard aesthetic). */

export const PORTFOLIO_WEBSITE_FILES = {
  'src/index.css': `@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,500;0,600;0,700;1,500&family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;1,9..40,400&display=swap');

html { scroll-behavior: smooth; }
body { font-family: 'DM Sans', system-ui, sans-serif; }
.font-display { font-family: 'Cormorant Garamond', Georgia, serif; }
`,

  'src/App.js': `import React from 'react';
import './index.css';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import About from './components/About';
import Projects from './components/Projects';
import Skills from './components/Skills';
import Contact from './components/Contact';
import Footer from './components/Footer';

export default function App() {
  return (
    <div className="min-h-screen bg-[#FFFBF7] text-[#3D3835] antialiased selection:bg-[#F0D4E4] selection:text-[#3D3835]">
      <Navbar />
      <main>
        <Hero />
        <About />
        <Projects />
        <Skills />
        <Contact />
      </main>
      <Footer />
    </div>
  );
}
`,

  'src/data/projects.js': `export const PROJECTS = [
  {
    title: 'Bloom Studio',
    desc: 'Brand identity and marketing site for an independent ceramics studio.',
    tags: ['Art Direction', 'Web Design'],
    accent: 'from-[#FFD6CC] to-[#F5D5C8]',
    span: 'md:col-span-7',
  },
  {
    title: 'Field Notes',
    desc: 'Editorial reading experience with custom typography and soft motion.',
    tags: ['UX', 'React'],
    accent: 'from-[#E8E0F0] to-[#D4C5E8]',
    span: 'md:col-span-5',
  },
  {
    title: 'Atelier Menu',
    desc: 'Minimal restaurant site with seasonal photography and reservation flow.',
    tags: ['UI Design', 'Framer'],
    accent: 'from-[#C8D9C8] to-[#E8F0E4]',
    span: 'md:col-span-5',
  },
  {
    title: 'Kindred App',
    desc: 'Wellness companion UI focused on calm palettes and gentle onboarding.',
    tags: ['Product Design', 'Figma'],
    accent: 'from-[#F0D4E4] to-[#FFD6CC]',
    span: 'md:col-span-7',
  },
];

export const SKILLS = {
  Design: ['Figma', 'Visual Systems', 'Typography', 'Prototyping', 'Brand'],
  Development: ['React', 'Tailwind CSS', 'Accessibility', 'Framer Motion'],
  Craft: ['Editorial Layout', 'Design Tokens', 'Responsive UI', 'Handoff'],
};

export const EXPERIENCE = [
  { role: 'Lead Product Designer', place: 'Studio North · 2022 — Present' },
  { role: 'UI Designer & Developer', place: 'Freelance · 2019 — 2022' },
];
`,

  'src/components/Navbar.js': `import React from 'react';

const links = [
  { label: 'About', href: '#about' },
  { label: 'Work', href: '#work' },
  { label: 'Skills', href: '#skills' },
  { label: 'Contact', href: '#contact' },
];

export default function Navbar() {
  return (
    <header className="sticky top-0 z-50 px-5 pt-5 md:px-10">
      <nav className="mx-auto flex max-w-6xl items-center justify-between rounded-full border border-[#F0E6DE] bg-white/75 px-5 py-3 shadow-[0_8px_32px_rgba(61,56,53,0.06)] backdrop-blur-md">
        <a href="#" className="font-display text-xl font-semibold tracking-tight text-[#3D3835]">
          Mira<span className="text-[#C49A8A]">.</span>lane
        </a>
        <div className="hidden items-center gap-8 md:flex">
          {links.map((l) => (
            <a
              key={l.label}
              href={l.href}
              className="text-sm font-medium text-[#6B6560] transition-colors hover:text-[#9BB89A]"
            >
              {l.label}
            </a>
          ))}
        </div>
        <a
          href="#contact"
          className="rounded-full bg-[#3D3835] px-4 py-2 text-xs font-semibold text-[#FFFBF7] transition hover:bg-[#5C554F]"
        >
          Say hello
        </a>
      </nav>
    </header>
  );
}
`,

  'src/components/Hero.js': `import React from 'react';

export default function Hero() {
  return (
    <section className="relative overflow-hidden px-5 pb-20 pt-10 md:px-10 md:pb-28 md:pt-16">
      <div className="pointer-events-none absolute -left-20 top-10 h-72 w-72 rounded-full bg-[#FFD6CC]/50 blur-3xl" />
      <div className="pointer-events-none absolute right-0 top-32 h-80 w-80 rounded-full bg-[#E8E0F0]/55 blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 left-1/3 h-64 w-64 rounded-full bg-[#C8D9C8]/40 blur-3xl" />

      <div className="relative mx-auto grid max-w-6xl items-center gap-14 md:grid-cols-12 md:gap-10">
        <div className="md:col-span-7 md:pr-6">
          <p className="mb-5 inline-flex items-center gap-2 rounded-full border border-[#E8DFD6] bg-white/80 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-[#9BB89A]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#9BB89A]" />
            Open to creative collaborations
          </p>
          <h1 className="font-display text-[2.75rem] font-semibold leading-[1.08] tracking-tight text-[#3D3835] md:text-[3.75rem]">
            Designing calm,{' '}
            <span className="italic text-[#C49A8A]">considered</span>
            <br />
            digital experiences
          </h1>
          <p className="mt-6 max-w-lg text-base leading-relaxed text-[#6B6560] md:text-lg">
            I&apos;m Mira Lane — a product designer & front-end developer crafting editorial portfolios,
            brand sites, and interfaces with soft color, generous space, and human warmth.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-4">
            <a
              href="#work"
              className="rounded-full bg-[#9BB89A] px-6 py-3 text-sm font-semibold text-white shadow-[0_10px_30px_rgba(155,184,154,0.35)] transition hover:bg-[#87A686]"
            >
              View selected work
            </a>
            <a
              href="#contact"
              className="rounded-full border border-[#E0D5CC] bg-white px-6 py-3 text-sm font-semibold text-[#5C554F] transition hover:border-[#D4C5E8] hover:text-[#3D3835]"
            >
              Get in touch
            </a>
          </div>
          <dl className="mt-12 flex flex-wrap gap-10">
            {[['8+', 'Years crafting UI'], ['32', 'Launched projects'], ['12', 'Happy collaborators']].map(([v, l]) => (
              <div key={l}>
                <dt className="font-display text-3xl font-semibold text-[#3D3835]">{v}</dt>
                <dd className="mt-1 text-sm text-[#9A928C]">{l}</dd>
              </div>
            ))}
          </dl>
        </div>

        <div className="relative md:col-span-5 md:col-start-8">
          <div className="absolute -right-4 -top-4 h-full w-full rounded-[2rem] bg-[#F0D4E4]/40" />
          <div className="relative overflow-hidden rounded-[2rem] border border-[#F0E6DE] bg-[#FFF8F3] p-8 shadow-[0_24px_60px_rgba(61,56,53,0.08)]">
            <div className="mx-auto flex h-52 w-52 items-center justify-center rounded-full border-2 border-dashed border-[#E8DFD6] bg-gradient-to-br from-[#FFD6CC]/30 to-[#E8E0F0]/40 md:h-56 md:w-56">
              <span className="text-center text-sm font-medium text-[#9A928C]">
                Your photo
                <br />
                <span className="text-xs">or illustration</span>
              </span>
            </div>
            <p className="mt-6 text-center font-display text-xl text-[#3D3835]">Mira Lane</p>
            <p className="text-center text-sm text-[#9A928C]">Designer · Developer · Creative partner</p>
          </div>
        </div>
      </div>
    </section>
  );
}
`,

  'src/components/About.js': `import React from 'react';
import { EXPERIENCE } from '../data/projects';

export default function About() {
  return (
    <section id="about" className="px-5 py-20 md:px-10 md:py-28">
      <div className="mx-auto max-w-6xl">
        <div className="grid gap-12 md:grid-cols-12 md:items-start">
          <div className="md:col-span-5">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C49A8A]">About</p>
            <h2 className="font-display mt-3 text-4xl font-semibold leading-tight text-[#3D3835] md:text-5xl">
              A studio-minded approach to product & brand
            </h2>
          </div>
          <div className="md:col-span-7">
            <div className="rounded-3xl border border-[#F0E6DE] bg-white p-8 shadow-[0_16px_48px_rgba(61,56,53,0.05)] md:p-10">
              <p className="text-base leading-relaxed text-[#6B6560] md:text-lg">
                I blend editorial layout sensibilities with pragmatic front-end craft — shaping interfaces
                that feel personal, breathable, and quietly premium. My work spans identity-led marketing
                sites, product UI, and design systems for small teams who care about detail.
              </p>
              <p className="mt-5 text-base leading-relaxed text-[#9A928C]">
                Based in Portland, collaborating remotely with studios, founders, and creative teams worldwide.
              </p>
              <ul className="mt-8 space-y-4 border-t border-[#F5EDE6] pt-8">
                {EXPERIENCE.map((e) => (
                  <li key={e.place} className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between">
                    <span className="font-semibold text-[#3D3835]">{e.role}</span>
                    <span className="text-sm text-[#9A928C]">{e.place}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
`,

  'src/components/Projects.js': `import React from 'react';
import { PROJECTS } from '../data/projects';

export default function Projects() {
  return (
    <section id="work" className="bg-[#FFF8F3] px-5 py-20 md:px-10 md:py-28">
      <div className="mx-auto max-w-6xl">
        <div className="mb-14 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#9BB89A]">Selected work</p>
            <h2 className="font-display mt-3 max-w-md text-4xl font-semibold text-[#3D3835] md:text-5xl">
              Projects with a soft, editorial presence
            </h2>
          </div>
          <p className="max-w-sm text-sm leading-relaxed text-[#9A928C] md:text-right">
            A curated showcase — brand sites, product UI, and experiential layouts built with intention.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-12">
          {PROJECTS.map((proj, i) => (
            <article
              key={proj.title}
              className={'group flex flex-col overflow-hidden rounded-3xl border border-[#F0E6DE] bg-white shadow-[0_12px_40px_rgba(61,56,53,0.06)] transition duration-300 hover:-translate-y-1 hover:border-[#E8E0F0] hover:shadow-[0_20px_50px_rgba(212,197,232,0.25)] ' + proj.span}
            >
              <div className={'relative flex h-44 items-center justify-center bg-gradient-to-br ' + proj.accent + (i % 2 === 0 ? ' md:h-52' : ' md:h-40')}>
                <div className="absolute inset-6 rounded-2xl border border-white/50 bg-white/30 backdrop-blur-sm" />
                <span className="relative font-display text-2xl text-[#3D3835]/80">{proj.title}</span>
              </div>
              <div className="flex flex-1 flex-col p-6 md:p-7">
                <h3 className="font-display text-2xl font-semibold text-[#3D3835]">{proj.title}</h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-[#6B6560]">{proj.desc}</p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {proj.tags.map((t) => (
                    <span
                      key={t}
                      className="rounded-full bg-[#F7F0E8] px-3 py-1 text-xs font-medium text-[#5C554F]"
                    >
                      {t}
                    </span>
                  ))}
                </div>
                <span className="mt-5 text-sm font-semibold text-[#C49A8A] transition group-hover:text-[#9BB89A]">
                  View case study →
                </span>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
`,

  'src/components/Skills.js': `import React from 'react';
import { SKILLS } from '../data/projects';

const PASTEL = {
  Design: 'bg-[#F0D4E4] text-[#6B4F5F] border-[#F0D4E4]',
  Development: 'bg-[#E8E0F0] text-[#5C5470] border-[#E8E0F0]',
  Craft: 'bg-[#E8F0E4] text-[#4F5F4E] border-[#E8F0E4]',
};

export default function Skills() {
  return (
    <section id="skills" className="px-5 py-20 md:px-10 md:py-28">
      <div className="mx-auto max-w-6xl text-center">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C49A8A]">Capabilities</p>
        <h2 className="font-display mx-auto mt-3 max-w-lg text-4xl font-semibold text-[#3D3835] md:text-5xl">
          Tools & craft, lightly organized
        </h2>
        <p className="mx-auto mt-4 max-w-md text-sm text-[#9A928C]">
          No feature grids — just the disciplines that shape my work.
        </p>

        <div className="mt-14 grid gap-8 text-left md:grid-cols-3">
          {Object.entries(SKILLS).map(([cat, items]) => (
            <div
              key={cat}
              className="rounded-3xl border border-[#F0E6DE] bg-white/80 p-7 shadow-[0_8px_28px_rgba(61,56,53,0.04)]"
            >
              <h3 className="font-display text-xl font-semibold text-[#3D3835]">{cat}</h3>
              <div className="mt-5 flex flex-wrap gap-2">
                {items.map((sk) => (
                  <span
                    key={sk}
                    className={'rounded-full border px-3 py-1.5 text-xs font-medium ' + (PASTEL[cat] || PASTEL.Design)}
                  >
                    {sk}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
`,

  'src/components/Contact.js': `import React from 'react';

export default function Contact() {
  return (
    <section id="contact" className="px-5 pb-24 pt-8 md:px-10">
      <div className="relative mx-auto max-w-6xl overflow-hidden rounded-[2rem] border border-[#F0E6DE] bg-white px-6 py-12 shadow-[0_20px_60px_rgba(61,56,53,0.07)] md:px-14 md:py-16">
        <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-[#FFD6CC]/40 blur-2xl" />
        <div className="pointer-events-none absolute -bottom-12 -left-12 h-40 w-40 rounded-full bg-[#D4C5E8]/35 blur-2xl" />

        <div className="relative grid gap-12 md:grid-cols-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#9BB89A]">Contact</p>
            <h2 className="font-display mt-3 text-4xl font-semibold text-[#3D3835] md:text-5xl">
              Let&apos;s shape something beautiful
            </h2>
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-[#6B6560]">
              Share a brief, a mood board, or a half-formed idea — I&apos;ll reply within two business days.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              {['hello@miralane.studio', 'Dribbble', 'LinkedIn'].map((s) => (
                <span
                  key={s}
                  className="rounded-full border border-[#F0E6DE] bg-[#FFFBF7] px-4 py-2 text-xs font-medium text-[#6B6560]"
                >
                  {s}
                </span>
              ))}
            </div>
          </div>

          <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
            <div className="grid gap-4 sm:grid-cols-2">
              <input
                placeholder="Your name"
                className="w-full rounded-2xl border border-[#EDE4DC] bg-[#FFFBF7] px-4 py-3 text-sm text-[#3D3835] outline-none transition placeholder:text-[#B8B0A8] focus:border-[#D4C5E8] focus:ring-2 focus:ring-[#E8E0F0]"
              />
              <input
                type="email"
                placeholder="you@email.com"
                className="w-full rounded-2xl border border-[#EDE4DC] bg-[#FFFBF7] px-4 py-3 text-sm text-[#3D3835] outline-none transition placeholder:text-[#B8B0A8] focus:border-[#D4C5E8] focus:ring-2 focus:ring-[#E8E0F0]"
              />
            </div>
            <textarea
              rows={4}
              placeholder="Tell me about your project..."
              className="w-full resize-y rounded-2xl border border-[#EDE4DC] bg-[#FFFBF7] px-4 py-3 text-sm text-[#3D3835] outline-none transition placeholder:text-[#B8B0A8] focus:border-[#D4C5E8] focus:ring-2 focus:ring-[#E8E0F0]"
            />
            <button
              type="submit"
              className="w-full rounded-full bg-[#3D3835] py-3.5 text-sm font-semibold text-[#FFFBF7] transition hover:bg-[#5C554F] sm:w-auto sm:px-10"
            >
              Send message
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
`,

  'src/components/Footer.js': `import React from 'react';

export default function Footer() {
  return (
    <footer className="border-t border-[#F0E6DE] bg-[#FFF8F3] px-5 py-10 md:px-10">
      <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-6 text-center md:flex-row md:text-left">
        <div>
          <p className="font-display text-lg font-semibold text-[#3D3835]">Mira Lane</p>
          <p className="mt-1 text-xs text-[#9A928C]">Personal portfolio · Crafted with care</p>
        </div>
        <p className="text-xs text-[#B8B0A8]">© 2025 · miralane.studio</p>
        <div className="flex gap-6 text-xs font-medium text-[#9A928C]">
          <a href="#about" className="hover:text-[#9BB89A]">About</a>
          <a href="#work" className="hover:text-[#9BB89A]">Work</a>
          <a href="#contact" className="hover:text-[#C49A8A]">Contact</a>
        </div>
      </div>
    </footer>
  );
}
`,
};
